import { AlertTriangle, CheckCircle2, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { StoryImportJob } from "@/features/story-import/story-import.types";
import {
  getStoryImportProgressValue,
  getStoryImportJobErrorTranslationKey,
  getStoryImportStatusKey,
} from "@/features/story-import/storyImportMappers";

interface StoryImportProcessingStepProps {
  isCanceling?: boolean;
  isRefreshing?: boolean;
  job: StoryImportJob | null;
  onCancel?: () => void;
  onOpenReview?: () => void;
  onRetry?: () => void;
}

export function StoryImportProcessingStep({
  isCanceling = false,
  isRefreshing = false,
  job,
  onCancel,
  onOpenReview,
  onRetry,
}: StoryImportProcessingStepProps) {
  const { t } = useTranslation();
  const isCanceled = job?.status === "canceled";
  const isFailed = job?.status === "failed";
  const isComplete = job?.status === "review_ready";
  const isCancelable =
    job?.status === "queued"
    || job?.status === "pending"
    || job?.status === "processing"
    || job?.status === "canceling";
  const progressValue = getStoryImportProgressValue(job);

  return (
    <Card>
      <CardContent className="space-y-5 p-8">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
            {t("storyImport.processing.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">{t("storyImport.processing.title")}</h2>
          <p className="max-w-3xl text-base leading-7 text-muted-foreground">
            {t("storyImport.processing.body")}
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-[1.5rem] border border-ink/10 bg-white/70 px-5 py-4">
          {isFailed ? (
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          ) : isCanceled ? (
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          ) : isComplete ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          ) : (
            <LoaderCircle className="h-5 w-5 animate-spin text-clay" />
          )}
          <div className="space-y-1">
            <p className="font-semibold text-foreground">
              {t("storyImport.processing.currentStatus", {
                status: t(getStoryImportStatusKey(job?.status ?? "processing")),
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("storyImport.processing.jobId", {
                jobId: job?.jobId ?? "pending",
              })}
            </p>
            <p className="text-sm text-muted-foreground">
                {t("storyImport.processing.passProgress", {
                  current: progressValue,
                  total: job?.totalPasses ?? 12,
                })}
            </p>
            {job?.detectedLanguage ? (
              <p className="text-sm text-muted-foreground">
                {t("storyImport.processing.detectedLanguage", {
                  language: job.detectedLanguage,
                })}
              </p>
            ) : null}
            {job?.passLabel ? (
              <p className="text-sm text-muted-foreground">{job.passLabel}</p>
            ) : null}
            {isRefreshing ? (
              <p className="text-sm text-muted-foreground">
                {t("storyImport.processing.refreshing")}
              </p>
            ) : null}
          </div>
        </div>

        {!isFailed && !isCanceled ? (
          <div className="rounded-[1.5rem] border border-ink/10 bg-canvas/75 px-5 py-4">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-clay">
              {t("storyImport.processing.faithfulTitle")}
            </p>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
              {t("storyImport.processing.faithfulBody")}
            </p>
          </div>
        ) : null}

        {isFailed ? (
          <div className="space-y-4 rounded-[1.5rem] border border-rose-200/70 bg-rose-50/90 px-5 py-4 text-sm text-rose-950">
            <p className="font-semibold">{t("storyImport.processing.failureTitle")}</p>
            <p>{t(getStoryImportJobErrorTranslationKey(job?.errorText))}</p>
            {onRetry ? (
              <Button onClick={onRetry} type="button" variant="outline">
                {t("storyImport.actions.retry")}
              </Button>
            ) : null}
          </div>
        ) : null}

        {isCanceled ? (
          <div className="space-y-4 rounded-[1.5rem] border border-amber-200/70 bg-amber-50/90 px-5 py-4 text-sm text-amber-950">
            <p className="font-semibold">{t("storyImport.processing.canceledTitle")}</p>
            <p>{t("storyImport.messages.processingCanceled")}</p>
          </div>
        ) : null}

        {(isCancelable && onCancel) || (isComplete && onOpenReview) ? (
          <div className="flex justify-end gap-3">
            {isCancelable && onCancel ? (
              <Button
                disabled={isCanceling || job?.status === "canceling"}
                onClick={onCancel}
                type="button"
                variant="outline"
              >
                {t("storyImport.actions.cancelImport")}
              </Button>
            ) : null}
            {isComplete && onOpenReview ? (
              <Button onClick={onOpenReview} type="button">
                {t("storyImport.actions.openReview")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
